import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { customerPhotosCall } from '../services/api';
import { toTitleCase } from '../utils/format';
import { BRANDS } from '../lib/brands';

// Marketing > Customer Photos (Greg, Oct 13, 2026). Where the Fall Crab Photo
// Contest entries (and "share your feast" photos) are judged: look at them all,
// pick winners, and send the good ones to the Media Library and to Dotdigital's
// image library. Backed by netlify/functions/customer-photos.js.

const STATUS = {
  new: { label: 'New', color: 'var(--gray-500)', bg: 'var(--gray-100)' },
  shortlist: { label: 'Shortlist', color: '#8a5a00', bg: '#fff4d6' },
  winner: { label: 'Winner', color: '#0b8043', bg: '#e3f7ea' },
  passed: { label: 'Passed', color: 'var(--gray-500)', bg: 'var(--gray-100)' },
};
// One tab per source (Greg, Oct 20, 2026): each contest gets its own tab as it comes
// (Fall Crab, then Thanksgiving, Christmas...), and every "Ask for photos" upload goes in
// Media Library requests. A new contest's tab appears by itself once it has an entry;
// give it a short name here.
const SOURCE_TABS = {
  'fall-crab-2026': 'Fall Crab Contest',
  'guest-share': 'Media Library requests',
  '': 'Share your feast',
};
const REQUESTS = 'guest-share';

const day = (v) => (v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const btn = (active) => ({
  fontSize: 12, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
  border: '1px solid ' + (active ? 'var(--navy)' : 'var(--gray-300)'),
  background: active ? 'var(--navy)' : 'white', color: active ? 'white' : 'var(--navy)',
});
const link = { fontSize: 11.5, color: 'var(--orange)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 0, padding: 0 };

export default function CustomerPhotos({ navigateTo }) {
  const [entries, setEntries] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('all');
  const [campaign, setCampaign] = useState(null); // source tab; null = pick the first
  const [busy, setBusy] = useState({});      // "lib:key" / "dd:key" / "st:id" -> true
  const [flash, setFlash] = useState({});    // key -> error text from the last move
  // Brand per entry. Guest uploads have none until someone picks one here (Greg, Oct 20, 2026);
  // contest entries keep going in as Crab Place.
  const [brandFor, setBrandFor] = useState({});
  const brandOf = (e) => (brandFor[e.id] !== undefined ? brandFor[e.id] : (e.isGuest ? '' : 'crab-place'));
  // Guest entries: the event (a tag) and any extra tags the reviewer adds before approving.
  const [eventFor, setEventFor] = useState({});
  const [moreTags, setMoreTags] = useState({});
  const [tagMsg, setTagMsg] = useState({});
  const tagOf = (t) => String(t || '').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const extraTagsOf = (e) => String(moreTags[e.id] || '').split(',').map(tagOf).filter(Boolean);
  const autoTagsOf = (e) => ['customer-photo', 'guest-share', 'guest-photo', e.eventLabel ? tagOf(e.eventLabel) : null].filter(Boolean);
  const saveEvent = async (e) => {
    const next = String(eventFor[e.id] !== undefined ? eventFor[e.id] : (e.eventLabel || '')).trim();
    if (next === (e.eventLabel || '')) return;
    try {
      await customerPhotosCall({ action: 'set-event', id: e.id, event: next });
      setEntries((list) => list.map((x) => (x.id === e.id ? { ...x, eventLabel: next || null, campaignLabel: 'Media Library request' + (next ? ': ' + next : '') } : x)));
    } catch (err) { setErr(err.message); }
  };
  const retag = async (e) => {
    setTagMsg((m) => ({ ...m, [e.id]: 'Adding tags...' }));
    try {
      await saveEvent(e); // a just-typed event must be saved first: it is one of the tags
      const r = await customerPhotosCall({ action: 'retag-library', id: e.id, tags: extraTagsOf(e) });
      setTagMsg((m) => ({ ...m, [e.id]: r.changed ? `Tagged ${r.changed} item${r.changed === 1 ? '' : 's'} already in the library.` : 'Those items already have these tags.' }));
    } catch (err) { setTagMsg((m) => ({ ...m, [e.id]: err.message })); }
  };

  const load = useCallback(() => customerPhotosCall({ action: 'list' })
    .then((r) => setEntries(r.entries || [])).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const patchPhoto = (id, key, patch) => setEntries((list) => list.map((e) => (e.id !== id ? e
    : { ...e, photos: e.photos.map((p) => (p.key === key ? { ...p, ...patch } : p)), videos: e.videos.map((v) => (v.key === key ? { ...v, ...patch } : v)) })));

  const move = async (entry, photo, where) => {
    if (entry.isGuest && where !== 'dotdigital') await saveEvent(entry);
    const tag = (where === 'dotdigital' ? 'dd:' : 'lib:') + photo.key;
    setBusy((b) => ({ ...b, [tag]: true })); setFlash((f) => ({ ...f, [photo.key]: null }));
    try {
      const action = where === 'library' ? 'to-library' : where === 'video' ? 'video-to-library' : 'to-dotdigital';
      const r = await customerPhotosCall({ action, id: entry.id, key: photo.key, brand: brandOf(entry), tags: extraTagsOf(entry) });
      patchPhoto(entry.id, photo.key, where !== 'dotdigital'
        ? { mediaAssetId: r.mediaAssetId, libraryAt: new Date().toISOString() }
        : { dotdigitalPath: r.dotdigitalPath, dotdigitalAt: new Date().toISOString() });
      return true;
    } catch (e) {
      setFlash((f) => ({ ...f, [photo.key]: (where === 'dotdigital' ? 'Dotdigital: ' : 'Media Library: ') + e.message }));
      return false;
    } finally { setBusy((b) => { const n = { ...b }; delete n[tag]; return n; }); }
  };

  // One photo at a time, in order: each move waits on another service, and doing
  // them one by one means every photo shows its own result.
  const sendAll = async (entry) => {
    for (const p of entry.photos) {
      if (!p.libraryAt) await move(entry, p, 'library');       // eslint-disable-line no-await-in-loop
      if (!p.dotdigitalAt) await move(entry, p, 'dotdigital'); // eslint-disable-line no-await-in-loop
    }
  };

  // Guests: approve everything in one go, Media Library only.
  const allToLibrary = async (entry) => {
    for (const p of entry.photos) { if (!p.libraryAt) await move(entry, p, 'library'); }   // eslint-disable-line no-await-in-loop
    for (const v of entry.videos) { if (!v.libraryAt) await move(entry, v, 'video'); }     // eslint-disable-line no-await-in-loop
  };

  const setStatus = async (entry, status) => {
    setBusy((b) => ({ ...b, ['st:' + entry.id]: true }));
    try {
      const r = await customerPhotosCall({ action: 'set-status', id: entry.id, status });
      setEntries((list) => list.map((e) => (e.id === entry.id ? { ...e, status: r.entry.status, reviewedBy: r.entry.reviewed_by, reviewedAt: r.entry.reviewed_at } : e)));
      // Picking a winner is the moment the photos are wanted everywhere, so send them.
      if (status === 'winner') await sendAll(entry);
    } catch (e) { setErr(e.message); } finally { setBusy((b) => { const n = { ...b }; delete n['st:' + entry.id]; return n; }); }
  };

  const saveNote = async (entry) => {
    const note = window.prompt('Note for the team about this entry:', entry.staffNote || '');
    if (note === null) return;
    try {
      const r = await customerPhotosCall({ action: 'set-status', id: entry.id, status: entry.status, note });
      setEntries((list) => list.map((e) => (e.id === entry.id ? { ...e, staffNote: r.entry.notes } : e)));
    } catch (e) { setErr(e.message); }
  };

  // Contests newest first (entries arrive newest first), Media Library requests last.
  const sources = useMemo(() => {
    const seen = new Map();
    for (const e of (entries || [])) {
      const k = e.campaign || '';
      if (!seen.has(k)) seen.set(k, { key: k, label: SOURCE_TABS[k] || e.campaignLabel || k, n: 0, fresh: 0 });
      const t = seen.get(k); t.n += 1; if (e.status === 'new') t.fresh += 1;
    }
    const list = [...seen.values()];
    return [...list.filter((t) => t.key !== REQUESTS), ...list.filter((t) => t.key === REQUESTS)];
  }, [entries]);
  const current = campaign !== null && sources.some((t) => t.key === campaign) ? campaign : (sources[0] ? sources[0].key : '');
  const isRequests = current === REQUESTS;
  const inCampaign = (entries || []).filter((e) => (e.campaign || '') === current);
  const count = (s) => inCampaign.filter((e) => e.status === s).length;

  const removeEntry = async (e, ask = true) => {
    const inLib = [...e.photos, ...e.videos].filter((p) => p.libraryAt).length;
    if (ask && !window.confirm(`Delete ${toTitleCase(e.name || '') || e.email}'s entry #${e.id}? Their uploaded photos and videos are removed.`
      + (inLib ? `\n\nThe ${inLib} already added to the Media Library stay there.` : ''))) return false;
    try {
      await customerPhotosCall({ action: 'delete', id: e.id });
      setEntries((list) => list.filter((x) => x.id !== e.id));
      return true;
    } catch (err) { setErr(err.message); return false; }
  };
  const removePassed = async () => {
    const passed = inCampaign.filter((e) => e.status === 'passed');
    if (!passed.length || !window.confirm(`Delete all ${passed.length} passed entr${passed.length === 1 ? 'y' : 'ies'} in this tab? Anything already in the Media Library stays there.`)) return;
    for (const e of passed) await removeEntry(e, false); // eslint-disable-line no-await-in-loop
  };
  const list = inCampaign.filter((e) => tab === 'all' || e.status === tab);
  const photoCount = inCampaign.reduce((n, e) => n + e.photos.length, 0);

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, color: 'var(--navy)' }}>Customer Photos</h2>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
          {isRequests
            ? <>Photos and videos people sent back from an &quot;Ask for photos&quot; link. Pick a brand, then add the ones you want (or all of them) to the Media Library. Nothing goes in until you do.</>
            : <>Photos customers sent us, with permission to use them. Pick a winner and its photos go to the Media Library and to the
              &quot;Fall Crab Contest&quot; folder in Dotdigital. The customer&apos;s original is never changed.</>}
        </div>
      </div>

      {err && <div className="card" style={{ padding: 12, color: 'var(--red)', fontSize: 13, marginBottom: 10 }}>Error: {err}</div>}
      {!entries && !err && <div className="card" style={{ padding: 16, color: 'var(--gray-500)', fontSize: 13 }}>Loading...</div>}

      {entries && (
        <>
          <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--gray-200)', marginBottom: 12, overflowX: 'auto' }}>
            {sources.map((t) => (
              <button key={t.key || 'none'} onClick={() => { setCampaign(t.key); setTab('all'); }}
                style={{ padding: '9px 14px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', background: 'none', border: 'none',
                  borderBottom: `3px solid ${t.key === current ? 'var(--orange)' : 'transparent'}`, marginBottom: -2,
                  color: t.key === current ? 'var(--navy)' : 'var(--gray-500)' }}>
                {t.label} <span style={{ fontWeight: 600, color: 'var(--gray-500)' }}>({t.n})</span>
                {t.fresh ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--orange)', borderRadius: 9, padding: '1px 7px' }}>{t.fresh} new</span> : null}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            {(isRequests
              ? [['all', `All (${inCampaign.length})`], ['new', `New (${count('new')})`], ['passed', `Passed (${count('passed')})`]]
              : [['all', `All (${inCampaign.length})`], ['new', `New (${count('new')})`], ['shortlist', `Shortlist (${count('shortlist')})`],
                ['winner', `Winners (${count('winner')})`], ['passed', `Passed (${count('passed')})`]]).map(([k, label]) => (
              <button key={k} style={btn(tab === k)} onClick={() => setTab(k)}>{label}</button>
            ))}
            {tab === 'passed' && count('passed') > 0 && (
              <button style={{ ...btn(false), color: 'var(--red)', borderColor: 'var(--red)' }} onClick={removePassed}>Delete all {count('passed')} passed</button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gray-500)' }}>{inCampaign.length} entries, {photoCount} photos{isRequests ? `, ${inCampaign.reduce((n, e) => n + e.videos.length, 0)} videos` : ''}</span>
          </div>

          {list.length === 0 && <div className="card" style={{ padding: 16, fontSize: 13, color: 'var(--gray-500)' }}>Nothing here yet.</div>}

          {list.map((e) => {
            const st = STATUS[e.status] || STATUS.new;
            const working = !!busy['st:' + e.id];
            const allKeys = e.photos.map((p) => encodeURIComponent(p.key)).join(',');
            const pending = e.photos.filter((p) => !p.libraryAt || !p.dotdigitalAt).length;
            return (
              <div key={e.id} className="card" style={{ padding: 14, marginBottom: 12, borderLeft: `4px solid ${e.status === 'winner' ? '#0b8043' : e.status === 'shortlist' ? '#e0a100' : 'var(--gray-200)'}`, opacity: e.status === 'passed' ? 0.6 : 1 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>{toTitleCase(e.name || '') || e.email}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, color: st.color, background: st.bg }}>{st.label}</span>
                  <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                    #{e.id} · {e.campaignLabel} · {day(e.createdAt)}{e.location ? ` · ${e.location}` : ''}{e.invitedBy ? ` · invited by ${e.invitedBy}` : ''}
                    {e.orderNumber ? <> · Order <span style={{ ...link, fontSize: 12 }} onClick={() => navigateTo?.('order-details', e.orderNumber)}>{e.orderNumber}</span></> : ''}
                    {' · '}<a href={`mailto:${e.email}`} style={{ color: 'var(--gray-500)' }}>{e.email}</a>
                    {e.socialUrl ? <>{' · '}<a href={e.socialUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gray-500)' }}>Their post</a></> : ''}
                  </span>
                </div>
                {e.story && <div style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--navy)', fontStyle: 'italic' }}>&quot;{e.story}&quot;</div>}
                {e.staffNote && <div style={{ marginTop: 4, fontSize: 12, color: '#8a5a00' }}>Team note: {e.staffNote}</div>}
                {e.isGuest && (() => {
                  const waiting = e.photos.filter((p) => !p.libraryAt).length + e.videos.filter((v) => !v.libraryAt).length;
                  const anyBusy = [...e.photos, ...e.videos].some((p) => busy['lib:' + p.key]);
                  return (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10, padding: '8px 10px', background: '#f7f9fb', borderRadius: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>Brand for the Media Library</span>
                      <select value={brandOf(e)} onChange={(ev) => setBrandFor((b) => ({ ...b, [e.id]: ev.target.value }))}
                        style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid ' + (brandOf(e) ? 'var(--gray-300)' : 'var(--orange)') }}>
                        <option value="">Pick a brand...</option>
                        {BRANDS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                      </select>
                      {waiting > 0
                        ? <button style={{ ...btn(true), opacity: brandOf(e) && !anyBusy ? 1 : 0.5 }} disabled={!brandOf(e) || anyBusy} onClick={() => allToLibrary(e)}>
                            {anyBusy ? 'Adding...' : `Add all ${waiting} to Media Library`}
                          </button>
                        : <span style={{ fontSize: 12, color: '#0b8043', fontWeight: 600 }}>✓ All in the Media Library</span>}
                      <span style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>Or add the ones you want one at a time below.</span>
                      <div style={{ flexBasis: '100%', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>Event</span>
                        <input value={eventFor[e.id] !== undefined ? eventFor[e.id] : (e.eventLabel || '')} placeholder="Oct 18 photo shoot"
                          onChange={(ev) => setEventFor((m) => ({ ...m, [e.id]: ev.target.value }))} onBlur={() => saveEvent(e)}
                          onKeyDown={(ev) => { if (ev.key === 'Enter') ev.currentTarget.blur(); }}
                          style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid ' + (e.eventLabel ? 'var(--gray-300)' : 'var(--orange)'), width: 200 }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginLeft: 6 }}>More tags</span>
                        <input value={moreTags[e.id] || ''} placeholder="lobster, grilling" onChange={(ev) => setMoreTags((m) => ({ ...m, [e.id]: ev.target.value }))}
                          style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray-300)', width: 220 }} />
                        {(e.photos.filter((p) => p.libraryAt).length + e.videos.filter((v) => v.libraryAt).length) > 0 && (
                          <button style={link} onClick={() => retag(e)}>Add these tags to the {e.photos.filter((p) => p.libraryAt).length + e.videos.filter((v) => v.libraryAt).length} already in the library</button>
                        )}
                      </div>
                      <div style={{ flexBasis: '100%', fontSize: 11.5, color: 'var(--gray-500)' }}>
                        Tags they'll get: {[...new Set([...autoTagsOf(e), ...extraTagsOf(e)])].join(', ')}. The Media Library also adds its own from what it sees.
                        {tagMsg[e.id] ? <span style={{ marginLeft: 8, color: 'var(--navy)', fontWeight: 600 }}>{tagMsg[e.id]}</span> : null}
                      </div>
                    </div>
                  );
                })()}

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
                  {e.photos.map((p, i) => (
                    <div key={p.key} style={{ width: 230 }}>
                      <a href={e.photos.length > 1 ? `/.netlify/functions/r2-image?set=${allKeys}#p${i + 1}` : p.url} target="_blank" rel="noopener noreferrer">
                        <img src={p.url} alt={`Entry ${e.id}, photo ${i + 1}`} loading="lazy"
                          style={{ width: 230, height: 230, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--gray-200)', background: 'var(--gray-100)', display: 'block' }} />
                      </a>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 6 }}>
                        {p.libraryAt
                          ? <span style={{ fontSize: 11.5, color: '#0b8043', fontWeight: 600 }}>✓ In Media Library <span style={{ ...link, marginLeft: 4 }} onClick={() => navigateTo?.('media-library')}>open</span></span>
                          : <button style={{ ...link, opacity: brandOf(e) ? 1 : 0.5 }} disabled={!!busy['lib:' + p.key] || !brandOf(e)} title={brandOf(e) ? '' : 'Pick a brand first'} onClick={() => move(e, p, 'library')}>{busy['lib:' + p.key] ? 'Adding to Media Library...' : 'Add to Media Library'}</button>}
                        {p.dotdigitalAt
                          ? <span style={{ fontSize: 11.5, color: '#0b8043', fontWeight: 600 }}>✓ In Dotdigital, Fall Crab Contest</span>
                          : <button style={link} disabled={!!busy['dd:' + p.key]} onClick={() => move(e, p, 'dotdigital')}>{busy['dd:' + p.key] ? 'Sending to Dotdigital...' : 'Send to Dotdigital'}</button>}
                        <a style={link} href={p.url} download={`${(e.name || 'customer').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${e.id}-${i + 1}.jpg`}>Download</a>
                        {(flash[p.key] || p.lastError) && <span style={{ fontSize: 11, color: 'var(--red)' }}>{flash[p.key] || p.lastError}</span>}
                      </div>
                    </div>
                  ))}
                  {e.videos.map((v, i) => (
                    <div key={v.key} style={{ width: 230 }}>
                      <video src={v.url} controls preload="metadata" style={{ width: 230, height: 230, borderRadius: 8, background: '#000', display: 'block' }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 6 }}>
                        {v.libraryAt
                          ? <span style={{ fontSize: 11.5, color: '#0b8043', fontWeight: 600 }}>✓ In Media Library <span style={{ ...link, marginLeft: 4 }} onClick={() => navigateTo?.('media-library')}>open</span></span>
                          : <button style={{ ...link, opacity: brandOf(e) ? 1 : 0.5 }} disabled={!!busy['lib:' + v.key] || !brandOf(e)} title={brandOf(e) ? '' : 'Pick a brand first'} onClick={() => move(e, v, 'video')}>{busy['lib:' + v.key] ? 'Adding to Media Library...' : 'Add to Media Library'}</button>}
                        <a style={link} href={v.url} target="_blank" rel="noopener noreferrer">Open video {i + 1}</a>
                        {(flash[v.key] || v.lastError) && <span style={{ fontSize: 11, color: 'var(--red)' }}>{flash[v.key] || v.lastError}</span>}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
                  {!e.isGuest && <button style={{ ...btn(e.status === 'winner'), borderColor: '#0b8043', background: e.status === 'winner' ? '#0b8043' : 'white', color: e.status === 'winner' ? 'white' : '#0b8043' }}
                    disabled={working} onClick={() => setStatus(e, 'winner')}>{working ? 'Working...' : e.status === 'winner' ? '✓ Winner' : 'Pick as winner'}</button>}
                  {!e.isGuest && <button style={btn(e.status === 'shortlist')} disabled={working} onClick={() => setStatus(e, 'shortlist')}>Shortlist</button>}
                  <button style={btn(e.status === 'passed')} disabled={working} onClick={() => setStatus(e, 'passed')}>Pass</button>
                  {e.status !== 'new' && <button style={link} disabled={working} onClick={() => setStatus(e, 'new')}>Back to New</button>}
                  <button style={link} onClick={() => saveNote(e)}>{e.staffNote ? 'Edit note' : 'Add note'}</button>
                  <button style={{ ...link, color: 'var(--red)' }} onClick={() => removeEntry(e)}>Delete</button>
                  {!e.isGuest && pending > 0 && e.status !== 'passed' && (
                    <button style={link} onClick={() => sendAll(e)}>Send {e.photos.length > 1 ? 'all photos' : 'photo'} to Media Library and Dotdigital</button>
                  )}
                  {e.reviewedBy && e.status !== 'new' && <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 'auto' }}>{st.label} by {e.reviewedBy}, {day(e.reviewedAt)}</span>}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
